import type { StorePrivacySettings } from './kreator/storePrivacy';
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, Link } from 'react-router-dom';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Globe,
  Power,
  Paintbrush,
} from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { kreatorApi, engineApi, storeApi, type EngineIntegration, type Store } from '../lib/api';
import { KreatorCodeEditorSection } from './kreator/KreatorCodeEditorSection';
import { KreatorColorsSection } from './kreator/KreatorColorsSection';
import { KreatorTypographySection } from './kreator/KreatorTypographySection';
import { KreatorNavigationSection } from './kreator/KreatorNavigationSection';
import { KreatorFooterSection } from './kreator/KreatorFooterSection';
import { KreatorPageAppearanceSection } from './kreator/KreatorPageAppearanceSection';
import { KreatorShopSection } from './kreator/KreatorShopSection';
import { KreatorImportExportSection } from './kreator/KreatorImportExportSection';
import { KreatorAdvertorialsSection } from './kreator/KreatorAdvertorialsSection';
import { KreatorQuizzesSection } from './kreator/KreatorQuizzesSection';
import { KreatorProductsSection } from './kreator/KreatorProductsSection';
import { lazyRetry } from '../lib/lazy-retry';

const PageManagerSection = lazy(() => lazyRetry(() => import('./kreator/PageManagerSection')));
const ProductEditorPage = lazy(() => lazyRetry(() => import('./kreator/ProductEditorPage').then((module) => ({ default: module.ProductEditorPage }))));
const WpPageEditorPage = lazy(() => lazyRetry(() => import('./kreator/WpPageEditorPage').then((module) => ({ default: module.WpPageEditorPage }))));
const PageBuilderEditorPage = lazy(() => lazyRetry(() => import('./kreator/builder-v2/PageBuilderEditorPage')));
const FunnelsListSection = lazy(() => lazyRetry(() => import('./kreator/FunnelsListSection')));
const FunnelEditorPage = lazy(() => lazyRetry(() => import('./kreator/funnel/FunnelEditorPage')));
const BundlesListSection = lazy(() => lazyRetry(() => import('./kreator/BundlesListSection')));
const BundleEditorPage = lazy(() => lazyRetry(() => import('./kreator/bundle/BundleEditorPage')));
const BundleSettingsPage = lazy(() => lazyRetry(() => import('./kreator/bundle/BundleSettingsPage')));
const CountersListSection = lazy(() => lazyRetry(() => import('./kreator/CountersListSection')));
const CounterEditorPage = lazy(() => lazyRetry(() => import('./kreator/counter/CounterEditorPage')));
const QuizEditorPage = lazy(() => lazyRetry(() => import('./kreator/quiz/QuizEditorPage')));

type WpDiagnostic = {
  ok: boolean;
  step?: string;
  error?: string;
  wpUrl?: string;
  httpStatus?: number;
  message?: string;
};

function isTransientWpDiagnostic(diag: WpDiagnostic): boolean {
  if (diag.ok || diag.step !== 'wp_network') return false;
  return /nie odpowiedział|limit czasu|timeout/i.test(diag.error ?? '');
}

function LegacyCounterRedirect({ integrationId }: { integrationId: string }) {
  const { counterId } = useParams<{ counterId: string }>();
  return <Navigate to={`/kreator/${integrationId}/counters/${counterId ?? ''}`} replace />;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface FontConfig {
  family: string;
  subsets: string[];
  size: number;
  lineHeight: number;
  weight: number;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
}

export interface ResponsiveScaling {
  smallDesktop: number;
  tablet: number;
  phone: number;
}

export interface BuilderBlock {
  id: string;
  type: string;
  visible: boolean;
  config: Record<string, unknown>;
  /** For container blocks (e.g. content-columns): array of slots, each slot holds child blocks */
  children?: BuilderBlock[][];
}

export interface KreatorSettingsData {
  hasApiKey: boolean;
  settings: {
    backgroundColor: string;
    fontColor: string;
    hoverTextColor?: string;
    buttonBackgroundColor?: string;
    buttonBackgroundHoverColor?: string;
    buttonTextColor?: string;
    buttonTextHoverColor?: string;
    maxContainerWidth: number;
    pageLoadingAnimation: string;
    borderStyle: 'rounded' | 'square';
    borderRadius: number;
    columnGap: number | { desktop: number; tablet: number; mobile: number };
    customCss: string;
    customJs: string;
    containerPadding?: { desktop: number; tablet: number; mobile: number };
    noBgZeroPadding?: boolean;
    iconStrokeWidth?: number;
    iconDrawEffect?: boolean;
    iconDrawDuration?: number;
    privacy?: StorePrivacySettings;
    globalLinks?: {
      terms_url?: string;
      privacy_url?: string;
      shipping_url?: string;
      returns_url?: string;
      contact_url?: string;
      cookie_url?: string;
    };
  };
  colors: {
    primary: string;
    additional1: string;
    additional2: string;
    additional3: string;
    primaryGradient: { hex1: string; hex2: string } | null;
    additionalGradient: { hex1: string; hex2: string } | null;
  };
  typography: {
    logoFont: FontConfig;
    menuFont: FontConfig;
    dropdownLevel1Font: FontConfig;
    dropdownLevel2Font: FontConfig;
    productPriceRegularFont: FontConfig;
    productPriceSaleFont: FontConfig;
    productPriceUnitFont: FontConfig;
    productPriceBadgeFont: FontConfig;
    headingH1: FontConfig;
    headingH2: FontConfig;
    headingH3: FontConfig;
    headingH4: FontConfig;
    headingH5: FontConfig;
    headingH6: FontConfig;
    bodyFont: FontConfig;
    pageTitleFont: FontConfig;
    pageBreadcrumbsFont: FontConfig;
    responsive: {
      headingH1: ResponsiveScaling;
      headingH2: ResponsiveScaling;
      headingH3: ResponsiveScaling;
      headingH4: ResponsiveScaling;
      headingH5: ResponsiveScaling;
      headingH6: ResponsiveScaling;
      bodyFont: ResponsiveScaling;
      pageTitleFont: ResponsiveScaling;
      pageBreadcrumbsFont: ResponsiveScaling;
      logoFont: ResponsiveScaling;
      menuFont: ResponsiveScaling;
      dropdownLevel1Font: ResponsiveScaling;
      dropdownLevel2Font: ResponsiveScaling;
      productPriceRegularFont: ResponsiveScaling;
      productPriceSaleFont: ResponsiveScaling;
      productPriceUnitFont: ResponsiveScaling;
      productPriceBadgeFont: ResponsiveScaling;
    };
  };
  navigation: {
    logoLightUrl: string | null;
    logoDarkUrl: string | null;
    logoMaxHeightDesktop: number;
    logoMaxHeightMobile: number;
    paddingTop: number;
    paddingTopTablet: number;
    paddingTopMobile: number;
    paddingBottom: number;
    paddingBottomTablet: number;
    paddingBottomMobile: number;
    menuBarColor: string;
    transparency: number;
    blurEffect: boolean;
    blurAmount: number;
    hideOnScroll: boolean;
    hideMobileBurgerMenu?: boolean;
    bottomBorder: string;
    bottomBorderColor: string | null;
    smartMenuEnabled: boolean;
    promoBar: {
      enabled: boolean;
      backgroundColor: string;
      textColor: string;
      messages: Array<{
        text: string;
        linkUrl: string | null;
        linkText: string | null;
      }>;
      autoRotate: boolean;
      rotateIntervalSec: number;
      showArrows: boolean;
      animation: 'fade' | 'slide';
      alwaysVisible: boolean;
    };
  };
  footer: {
    templateId: string;
    customMode: 'default' | 'blank';
    columns: Array<{ title: string; links: Array<{ label: string; url: string }> }>;
    copyrightText: string;
    socialLinks: Array<{ platform: string; url: string }>;
    logoUrl: string | null;
    useCustomColors: boolean;
    fontColor: string | null;
    headingColor: string | null;
    backgroundColor: string | null;
    paddingTop: number;
    paddingBottom: number;
  };
  pageAppearance: {
    productPage: {
      galleryType: 'default' | 'classic' | 'vertical-scroll' | 'full-width';
      columnRatio: number;
      productPageColumnGap: number | null;
      showRelatedProducts: boolean;
      relatedProductsCount: number;
      showUpsells: boolean;
      showReviews: boolean;
      showTabs: boolean;
      tabsStyle: 'default' | 'integrated' | 'accordion' | 'vertical';
      addToCartButtonStyle: 'filled' | 'outline' | 'default' | 'rounded' | 'pill';
      addToCartButtonColor: string | null;
      addToCartShowQuantity: boolean;
      addToCartPulseEnabled: boolean;
      addToCartPulseColor: string | null;
      addToCartPulseOpacity: number;
      showSku: boolean;
      showCategories: boolean;
      stickyAddToCart: boolean;
      stickyCartMode?: 'direct' | 'package-picker';
      showOmnibusPrice: boolean;
      showSaleBadge: boolean;
      galleryLightbox: boolean;
    };
    cart: {
      layout: 'standard' | 'compact' | 'modern';
      showCouponField: boolean;
      showCrossSells: boolean;
      crossSellsCount: number;
      showContinueShopping: boolean;
      continueShoppingUrl: string;
      showEstimatedDelivery: boolean;
      showFreeShippingBar: boolean;
      freeShippingThreshold: number | null;
      autoUpdate?: boolean;
      showWooCommerceNotices?: boolean;
      showCartHeader?: boolean;
      reservationEnabled?: boolean;
      reservationDurationMinutes?: number;
      reservationMessage?: string;
      reservationExpiredMessage?: string;
      reservationHideExpired?: boolean;
      reservationResetMinutes?: number;
      compactFreeShippingEnabled?: boolean;
      compactCrossSellsEnabled?: boolean;
      compactCrossSellProductIds?: number[];
      compactCrossSellBadge?: {
        enabled: boolean;
        text: string;
        backgroundColor: string | null;
        textColor: string | null;
        borderColor: string | null;
        borderStyle: 'none' | 'solid' | 'dashed' | 'dotted';
        borderWidth: number;
        borderRadius: number;
      };
      compactCostSummaryEnabled?: boolean;
      compactCtaText?: string;
      compactCtaIcon?: 'arrow-right' | 'lock' | 'shopping-bag' | 'credit-card' | 'none';
      compactBenefits?: Array<{ text: string; icon: 'shield' | 'truck' | 'return' | 'lock' | 'star' | 'check' }>;
    };
    checkout: {
      layout: 'standard' | 'multi-step' | 'one-page' | 'delivery-first';
      showPromotion?: boolean;
      promotionLabel?: string;
      showCheckoutTimer?: boolean;
      trustBadges?: Array<{ id?: string; icon: string; text: string }>;
      showTrustBadges?: boolean;
      trustSecureText?: string;
      trustDispatchText?: string;
      trustSellerText?: string;

      returnsMessage?: string;
      simplifyPickupAddress?: boolean;
      pickupMapMode?: 'plugins' | 'ecomflow';
      showOrderNotes: boolean;
      showCompanyFields: boolean;
      showPhoneField: boolean;
      showCouponField: boolean;
      stepLabels: {
        shipping: string;
        payment: string;
        review: string;
      };
      termsCheckboxRequired: boolean;
      termsAcceptance?: {
        mode: 'checkbox' | 'cta';
        notice: string;
        termsLabel: string;
        termsUrl: string;
        privacyLabel: string;
        privacyUrl: string;
        showCompanyInfo: boolean;
        companyInfo: string;
        fontSize: number;
        alignment: 'left' | 'center';
      };
      expressCheckoutEnabled: boolean;
      checkoutFields?: import('../types/funnel.types').CheckoutFieldConfig[];
    };
    thankYouPage: {
      customHeading: string;
      customMessage: string;
      showOrderDetails: boolean;
      showRecommendedProducts: boolean;
      recommendedProductsCount: number;
      showReturnToShopButton: boolean;
      returnToShopUrl: string;
      showNewsletter: boolean;
    };
    shopArchive: {
      columns: number;
      productsPerPage: number;
      showSorting: boolean;
      showResultCount: boolean;
      showSidebar: boolean;
      productCardStyle: 'default' | 'minimal' | 'detailed';
      showAddToCartButton: boolean;
      showRating: boolean;
      showSaleBadge: boolean;
      imageAspectRatio: '1:1' | '4:3' | '3:4' | '16:9';
      hoverEffect: 'none' | 'zoom' | 'swap';
    };
    shop: {
      showAddToCartNotice: boolean;
    };
    pages: {
      bannerApplyMode: 'all' | 'selected';
      bannerSectionType: 'contained' | 'full-stretch';
      bannerBackgroundType: 'color' | 'image';
      bannerBackgroundColor: string;
      bannerBackgroundImageUrl: string | null;
      bannerTextColor: string;
      bannerTextAlign: 'left' | 'center' | 'right';
      bannerHeight: { desktop: number; tablet: number; mobile: number };
      bannerTopMargin: { desktop: number; mobile: number };
      bannerRadiusMode: 'theme' | 'custom';
      bannerBorderRadius: number;
      showBreadcrumbs: boolean;
    };
  };
  smartMenuProducts: Array<{
    wooProductId: number;
    logoUrl: string | null;
    logoMaxHeightDesktop?: number | null;
    logoMaxHeightMobile?: number | null;
    ctaText: string;
    ctaColor: string | null;
  }>;
  homepage: {
    wpPageId: number | null;
  } | null;
  blog: {
    wpPageId: number | null;
  } | null;
}

// ─── Toast Component ─────────────────────────────────────────────────────────

function KreatorToast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-fade-in transition-all duration-300">
      <div
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border backdrop-blur-sm ${
          type === 'success'
            ? 'bg-success/15 border-success/30 text-success'
            : 'bg-danger/15 border-danger/30 text-danger'
        }`}
      >
        {type === 'success' ? (
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
        )}
        <span className="text-sm font-medium whitespace-nowrap">{message}</span>
        <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100 transition-opacity text-lg leading-none">&times;</button>
      </div>
    </div>
  );
}

export function useKreatorToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showSuccess = useCallback((msg: string) => setToast({ message: msg, type: 'success' }), []);
  const showError = useCallback((msg: string) => setToast({ message: msg, type: 'error' }), []);
  const clearToast = useCallback(() => setToast(null), []);
  return { toast, showSuccess, showError, clearToast };
}

// ─── Store Cards Grid (Level 1) ─────────────────────────────────────────────

function KreatorStoreList() {
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    storeApi.getAll().then((res) => {
      setStores(res.data.filter(s => s.engineEnabled && s.engineIntegrationId));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <EmptyState
        icon={Paintbrush}
        title="Brak sklepów z Engine"
        description="Aby korzystać z Kreatora, włącz Engine w ustawieniach sklepu (zakładka Engine)."
        buttonLabel="Przejdź do Sklepów"
        buttonTo="/integrations/stores"
        iconColorClass="from-accent-primary/20 to-accent-info/20 border-accent-primary/20 text-accent-primary"
      />
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Paintbrush className="w-7 h-7 text-accent-primary" />
          Kreator
        </h1>
        <p className="text-text-secondary mt-1">Wybierz sklep, który chcesz konfigurować</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((store) => (
          <Link
            key={store.id}
            to={`/kreator/${store.engineIntegrationId}/funnels`}
            className="group card hover:border-accent-primary/30 transition-all duration-200 cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/20 transition-colors">
                <Globe className="w-6 h-6 text-accent-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-text-primary font-semibold truncate">{store.name}</h3>
                  <Power className="w-3.5 h-3.5 text-success flex-shrink-0" />
                </div>
                <p className="text-sm text-text-secondary truncate">{store.url}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function KreatorABTestsEntry() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    storeApi.getAll().then((res) => {
      setStores(res.data.filter(s => s.engineEnabled && s.engineIntegrationId));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading && stores.length === 1 && stores[0].engineIntegrationId) {
      navigate(`/kreator/${stores[0].engineIntegrationId}/funnels`, { replace: true });
    }
  }, [loading, navigate, stores]);

  if (loading || stores.length === 1) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <EmptyState
        icon={Paintbrush}
        title="Brak sklepów z Engine"
        description="A/B testy są częścią lejków Kreatora. Najpierw włącz Engine dla sklepu."
        buttonLabel="Przejdź do Sklepów"
        buttonTo="/integrations/stores"
        iconColorClass="from-accent-primary/20 to-accent-info/20 border-accent-primary/20 text-accent-primary"
      />
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Paintbrush className="w-7 h-7 text-accent-primary" />
          A/B testy w Kreatorze
        </h1>
        <p className="text-text-secondary mt-1">Wybierz sklep, którego lejki i testy chcesz sprawdzić.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((store) => (
          <Link
            key={store.id}
            to={`/kreator/${store.engineIntegrationId}/funnels`}
            className="group card hover:border-accent-primary/30 transition-all duration-200 cursor-pointer"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-primary/20 transition-colors">
                <Globe className="w-6 h-6 text-accent-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-text-primary font-semibold truncate">{store.name}</h3>
                  <Power className="w-3.5 h-3.5 text-success flex-shrink-0" />
                </div>
                <p className="text-sm text-text-secondary truncate">{store.url}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Store Config View (Level 2) ─────────────────────────────────────────────

function KreatorStoreConfig() {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<EngineIntegration | null>(null);
  const [kreatorSettings, setKreatorSettings] = useState<KreatorSettingsData | null>(null);
  const [wpDiag, setWpDiag] = useState<WpDiagnostic | null>(null);
  const [diagChecking, setDiagChecking] = useState(false);
  const { toast, showSuccess, showError, clearToast } = useKreatorToast();

  useEffect(() => {
    if (!integrationId) return;
    setLoading(true);
    Promise.all([
      engineApi.getAll(),
      kreatorApi.getSettings(integrationId),
    ]).then(([intRes, settingsRes]) => {
      const found = intRes.data.find((i: EngineIntegration) => i.id === integrationId);
      if (!found) {
        navigate('/kreator', { replace: true });
        return;
      }
      setIntegration(found);
      setKreatorSettings(settingsRes.data);

      // Check WP connection in the background
      kreatorApi.diagnoseWp(integrationId).then(res => {
        if (!isTransientWpDiagnostic(res.data)) {
          setWpDiag(res.data);
        }
      }).catch(() => {});
    }).catch(() => {
      showError('Nie udało się wczytać ustawień');
    }).finally(() => setLoading(false));
  }, [integrationId, navigate, showError]);

  const recheckWp = useCallback(async () => {
    if (!integrationId) return;
    setDiagChecking(true);
    try {
      const res = await kreatorApi.diagnoseWp(integrationId);
      setWpDiag(res.data);
      if (res.data.ok) {
        showSuccess('Połączenie z WordPress działa prawidłowo!');
      }
    } catch {
      // silent
    } finally {
      setDiagChecking(false);
    }
  }, [integrationId, showSuccess]);

  const refreshSettings = useCallback(async () => {
    if (!integrationId) return;
    try {
      const res = await kreatorApi.getSettings(integrationId);
      setKreatorSettings(res.data);
    } catch {
      // silent
    }
  }, [integrationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (!integration || !kreatorSettings || !integrationId) {
    return <Navigate to="/kreator" replace />;
  }

  const sectionProps = {
    integrationId,
    settings: kreatorSettings,
    onSaved: refreshSettings,
    showSuccess,
    showError,
  };
  const sectionFallback = (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
    </div>
  );

  return (
    <div className="animate-fade-in">
      {/* WordPress connection warning */}
      {wpDiag && !wpDiag.ok && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-300 mb-1">Połączenie z WordPress nie działa</p>
              <p className="text-sm text-amber-200/80">{wpDiag.error}</p>
              <p className="text-xs text-amber-200/50 mt-2">Zmiany w Kreatorze są zapisywane, ale nie pojawią się na stronie sklepu dopóki połączenie nie zostanie naprawione.</p>
            </div>
            <button
              onClick={recheckWp}
              disabled={diagChecking}
              className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
            >
              {diagChecking ? 'Sprawdzam...' : 'Sprawdź ponownie'}
            </button>
          </div>
        </div>
      )}

      {/* Section content */}
      <Routes>
        <Route path="settings" element={<Navigate to={`/kreator/${integrationId}/funnels`} replace />} />
        <Route path="code-editor" element={<KreatorCodeEditorSection {...sectionProps} />} />
        <Route path="colors" element={<KreatorColorsSection {...sectionProps} />} />
        <Route path="typography" element={<KreatorTypographySection {...sectionProps} />} />
        <Route path="navigation" element={<KreatorNavigationSection {...sectionProps} />} />
        <Route path="footer" element={<KreatorFooterSection {...sectionProps} />} />
        <Route path="shop" element={<KreatorShopSection {...sectionProps} storeId={integration.storeId} wordpressUrl={integration.wordpressUrl || integration.store?.url} />} />
        <Route path="pages" element={<KreatorPageAppearanceSection {...sectionProps} />} />
        <Route path="import-export" element={<KreatorImportExportSection {...sectionProps} />} />
        <Route path="advertorials" element={<KreatorAdvertorialsSection wordpressUrl={integration.wordpressUrl} />} />
        <Route path="quizzes" element={<KreatorQuizzesSection wordpressUrl={integration.wordpressUrl} />} />
        <Route path="quizzes/:quizId" element={<Suspense fallback={sectionFallback}><QuizEditorPage /></Suspense>} />
        <Route path="products" element={<KreatorProductsSection integrationId={integrationId} wordpressUrl={integration.wordpressUrl} showError={showError} />} />
        <Route path="products/new" element={<Suspense fallback={sectionFallback}><ProductEditorPage integrationId={integrationId} settings={kreatorSettings} onSettingsSaved={refreshSettings} showSuccess={showSuccess} showError={showError} /></Suspense>} />
        <Route path="products/:productId" element={<Suspense fallback={sectionFallback}><ProductEditorPage integrationId={integrationId} settings={kreatorSettings} onSettingsSaved={refreshSettings} showSuccess={showSuccess} showError={showError} /></Suspense>} />
        <Route path="builder/pages" element={<Suspense fallback={sectionFallback}><PageManagerSection wordpressUrl={integration.wordpressUrl} /></Suspense>} />
        <Route path="builder/:pageId" element={<Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-surface-base"><Loader2 className="w-8 h-8 animate-spin text-accent-primary" /></div>}><PageBuilderEditorPage /></Suspense>} />
        <Route path="wp-pages/new" element={<Suspense fallback={sectionFallback}><WpPageEditorPage integrationId={integrationId} settings={kreatorSettings} showSuccess={showSuccess} showError={showError} /></Suspense>} />
        <Route path="wp-pages/:pageId" element={<Suspense fallback={sectionFallback}><WpPageEditorPage integrationId={integrationId} settings={kreatorSettings} showSuccess={showSuccess} showError={showError} /></Suspense>} />
        <Route path="funnels" element={<Suspense fallback={sectionFallback}><FunnelsListSection integrationId={integrationId} /></Suspense>} />
        <Route path="funnels/:funnelId" element={<Suspense fallback={sectionFallback}><FunnelEditorPage integrationId={integrationId} /></Suspense>} />
        <Route path="counters" element={<Suspense fallback={sectionFallback}><CountersListSection integrationId={integrationId} /></Suspense>} />
        <Route path="counters/:counterId" element={<Suspense fallback={sectionFallback}><CounterEditorPage integrationId={integrationId} /></Suspense>} />
        <Route path="bundles" element={<Suspense fallback={sectionFallback}><BundlesListSection integrationId={integrationId} /></Suspense>} />
        <Route path="bundles/counters" element={<Navigate to={`/kreator/${integrationId}/counters`} replace />} />
        <Route path="bundles/counters/:counterId" element={<LegacyCounterRedirect integrationId={integrationId} />} />
        <Route path="bundles/settings" element={<Suspense fallback={sectionFallback}><BundleSettingsPage integrationId={integrationId} /></Suspense>} />
        <Route path="bundles/:bundleId" element={<Suspense fallback={sectionFallback}><BundleEditorPage integrationId={integrationId} /></Suspense>} />
        <Route path="*" element={<Navigate to="funnels" replace />} />
      </Routes>

      {/* Toast */}
      {toast && (
        <KreatorToast message={toast.message} type={toast.type} onClose={clearToast} />
      )}
    </div>
  );
}

// ─── Main Kreator Page (Router) ──────────────────────────────────────────────

export function KreatorPage() {
  return (
    <Routes>
      <Route index element={<KreatorStoreList />} />
      <Route path="ab-tests" element={<KreatorABTestsEntry />} />
      <Route path=":integrationId/*" element={<KreatorStoreConfig />} />
    </Routes>
  );
}
